import React from "react";

export const STATUS_LABELS = {
  PreAdmit: "Pre-Admit",
  Admit: "Admit",
  Active: "Active",
  Hold: "Hold",
  Inactive: "Inactive",
  Discharged: "Discharged",
  NonAdmit: "Non-Admit",
  Deceased: "Deceased",
};

/** Patient list / filter dropdown order. */
export const PATIENT_STATUS_FILTER_ORDER = [
  "PreAdmit",
  "Admit",
  "Active",
  "Hold",
  "Inactive",
  "Discharged",
  "NonAdmit",
  "Deceased",
];

export const CHART_STATUS_LABELS = {
  PreAdmit: "Pre-Admit",
  Admit: "Admitted",
  Active: "Active",
  Hold: "Hold",
  Inactive: "Inactive",
  Discharged: "Discharged",
  NonAdmit: "Non-Admit",
  Deceased: "Deceased",
};

/** SOC = start of care (API: admissionDate). EOC = end of care (API: eocDate). */
export const SOC_DATE_LABEL = "SOC date";
export const EOC_DATE_LABEL = "EOC date";

/** Statuses that show + require EOC date next to SOC date. */
export const STATUSES_REQUIRING_EOC = ["Inactive", "Discharged"];

export const statusRequiresEocDate = (status) =>
  status === "Inactive" || status === "Discharged";

export const isActiveCensusStatus = (status) =>
  status === "Admit" || status === "Active";

/** Shared select options for add/edit/demographics status fields. */
export const PATIENT_STATUS_SELECT_OPTIONS = PATIENT_STATUS_FILTER_ORDER.map(
  (value) => ({ value, label: STATUS_LABELS[value] }),
);

export const STATUS_STYLES = {
  PreAdmit: {
    wrap: "bg-sky-50 text-sky-800 border-sky-200",
    dot: "bg-sky-500",
    chevron: "text-sky-600/70",
    text: "text-sky-700",
  },
  Admit: {
    wrap: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot: "bg-emerald-500",
    chevron: "text-emerald-600/70",
    text: "text-emerald-700",
  },
  Active: {
    wrap: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot: "bg-emerald-500",
    chevron: "text-emerald-600/70",
    text: "text-emerald-700",
  },
  Hold: {
    wrap: "bg-orange-50 text-orange-800 border-orange-200",
    dot: "bg-orange-500",
    chevron: "text-orange-600/70",
    text: "text-orange-700",
  },
  Inactive: {
    wrap: "bg-slate-50 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
    chevron: "text-slate-400",
    text: "text-slate-700",
  },
  Discharged: {
    wrap: "bg-slate-50 text-slate-600 border-slate-200",
    dot: "bg-slate-400",
    chevron: "text-slate-400",
    text: "text-slate-700",
  },
  NonAdmit: {
    wrap: "bg-violet-50 text-violet-800 border-violet-200",
    dot: "bg-violet-500",
    chevron: "text-violet-600/70",
    text: "text-violet-700",
  },
  Deceased: {
    wrap: "bg-zinc-100 text-zinc-700 border-zinc-300",
    dot: "bg-zinc-500",
    chevron: "text-zinc-500",
    text: "text-zinc-700",
  },
};

export const isClosedChartStatus = (status) =>
  status === "Inactive" ||
  status === "Discharged" ||
  status === "NonAdmit" ||
  status === "Deceased";

const StatusBadge = ({
  status = "Admit",
  interactive = false,
  open = false,
  chartStyle = false,
  compact = false,
}) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.Hold;
  const labels = chartStyle ? CHART_STATUS_LABELS : STATUS_LABELS;
  const label = labels[status] || status || STATUS_LABELS.Hold;

  return (
    <span
      className={`inline-flex items-center gap-1 ${
        compact ? "h-5 pl-1.5 text-[10px]" : "h-6 pl-2 text-[11px]"
      } ${interactive ? "pr-1.5" : compact ? "pr-1.5" : "pr-2"} rounded border font-medium leading-none ${style.wrap} ${
        interactive && open ? "shadow-sm ring-2 ring-blue-200 ring-offset-0" : ""
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dot}`} />
      <span className={compact ? "whitespace-nowrap" : "min-w-[64px]"}>
        {label}
      </span>
      {interactive ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`shrink-0 ml-0.5 transition-transform ${style.chevron} ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      ) : null}
    </span>
  );
};

export default StatusBadge;
